'use client';

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { store } from '@/shared/store/store';
import { ScrollLockProvider } from '@/shared/ui/organisms/ContactModal/ScrollLockContext';
import { ContactModalProvider } from '@/shared/ui/organisms/ContactModal/ContactModalContext';
import { AnimatedBackground, Navbar } from '@/shared/ui';
import Preloader from '@/shared/ui/organisms/Preloader';

const PRELOADER_DONE_KEY = 'preloader_done';

function getPreloaderAlreadySeen(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        return sessionStorage.getItem(PRELOADER_DONE_KEY) === '1';
    } catch {
        return false;
    }
}

type PreloaderInit = {
    storageChecked: boolean;
    isAppReady: boolean;
};

export function Providers({ children }: { children: React.ReactNode }) {
    const [isPageLoaded, setIsPageLoaded] = useState(
        () => typeof document !== 'undefined' && document.readyState === 'complete',
    );
    const [init, setInit] = useState<PreloaderInit>({ storageChecked: false, isAppReady: false });

    useLayoutEffect(() => {
        const t = setTimeout(() => {
            setInit({
                storageChecked: true,
                isAppReady: getPreloaderAlreadySeen(),
            });
        }, 0);
        return () => clearTimeout(t);
    }, []);

    const handlePreloaderFinished = useCallback(() => {
        try {
            sessionStorage.setItem(PRELOADER_DONE_KEY, '1');
        } catch {
            // ignore
        }
        setInit(prev => ({ ...prev, isAppReady: true }));
    }, []);

    useEffect(() => {
        if (isPageLoaded) return;

        const handleLoad = () => {
            setIsPageLoaded(true);
        };

        window.addEventListener('load', handleLoad);
        return () => {
            window.removeEventListener('load', handleLoad);
        };
    }, [isPageLoaded]);

    const showContent = init.storageChecked && init.isAppReady;
    const showPreloader = init.storageChecked && !init.isAppReady;

    return (
        <Provider store={store}>
            <ScrollLockProvider>
                <ContactModalProvider>
                    {!init.storageChecked && (
                        <div className="min-h-screen w-full bg-black" aria-hidden />
                    )}
                    {showPreloader && (
                        <Preloader isDone={isPageLoaded} onFinished={handlePreloaderFinished} />
                    )}
                    {showContent && (
                        <>
                            <AnimatedBackground />
                            <Navbar />
                            <main>{children}</main>
                        </>
                    )}
                </ContactModalProvider>
            </ScrollLockProvider>
        </Provider>
    );
}
